import {
  BellRing,
  History,
  LayoutDashboard,
  LogOut,
  MessageSquarePlus,
  PanelLeft,
  RadioTower,
  Shield,
  Users,
  Wand2
} from "lucide-react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Dashboard } from "./pages/Dashboard";
import { Setup } from "./pages/Setup";
import { Login } from "./pages/Login";
import { CreateCard } from "./pages/CreateCard";
import { Drafts } from "./pages/Drafts";
import { Templates } from "./pages/Templates";
import { Scheduled } from "./pages/Scheduled";
import { HistoryPage } from "./pages/History";
import { Channels } from "./pages/Channels";
import { UsersPage } from "./pages/Users";
import { canManageChannels, canManageUsers, useAuth } from "./lib/auth";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/create", label: "Create Card", icon: MessageSquarePlus },
  { to: "/drafts", label: "Drafts", icon: PanelLeft },
  { to: "/templates", label: "Templates", icon: Wand2 },
  { to: "/scheduled", label: "Scheduled", icon: BellRing },
  { to: "/history", label: "History", icon: History },
  { to: "/channels", label: "Channels", icon: RadioTower, needsChannelRole: true },
  { to: "/users", label: "Users", icon: Users, needsUserRole: true }
];

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-panel">
      <div className="rounded border border-line bg-white px-5 py-4 text-sm font-medium text-slate-600 shadow-soft">
        Loading admin panel...
      </div>
    </div>
  );
}

function ProtectedLayout() {
  const { user, setupRequired, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (setupRequired) return <Navigate to="/setup" replace />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  const visibleNav = navItems.filter((item) => {
    if (item.needsUserRole) return canManageUsers(user);
    if (item.needsChannelRole) return canManageChannels(user);
    return true;
  });

  return (
    <div className="min-h-screen bg-panel text-ink">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-line bg-white lg:block">
        <div className="flex h-16 items-center gap-3 border-b border-line px-5">
          <div className="flex h-10 w-10 items-center justify-center rounded bg-brand text-white">
            <Shield size={22} />
          </div>
          <div>
            <div className="font-semibold leading-tight">Telegram Card</div>
            <div className="text-xs text-slate-500">Admin Panel</div>
          </div>
        </div>
        <nav className="space-y-1 p-3">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100 hover:text-ink"
                  }`
                }
              >
                <Icon size={18} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-10 flex min-h-16 items-center justify-between gap-3 border-b border-line bg-white/90 px-4 backdrop-blur lg:px-6">
          <div className="min-w-0">
            <div className="truncate text-sm text-slate-500">Signed in as {user.displayName || user.username}</div>
            <div className="text-xs uppercase tracking-wide text-brand">{user.role}</div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto lg:hidden">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex h-10 w-10 flex-none items-center justify-center rounded border ${
                      isActive ? "border-brand bg-brand text-white" : "border-line bg-white text-slate-600"
                    }`
                  }
                  title={item.label}
                >
                  <Icon size={18} />
                </NavLink>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => logout()}
            className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            <LogOut size={16} />
            Logout
          </button>
        </header>
        <main className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-6">
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/create" element={<CreateCard />} />
            <Route path="/drafts" element={<Drafts />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/scheduled" element={<Scheduled />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/channels" element={<Channels />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function PublicRoute({ children }: { children: JSX.Element }) {
  const { user, setupRequired, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  if (setupRequired && location.pathname !== "/setup") return <Navigate to="/setup" replace />;
  if (!setupRequired && location.pathname === "/setup") return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/setup"
        element={
          <PublicRoute>
            <Setup />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/*" element={<ProtectedLayout />} />
    </Routes>
  );
}
